import React from 'react';
import { string, number, bool, node, oneOfType } from 'prop-types';

/* Coordinates are written as either numbers or strings below. */
const num = oneOfType([number, string]);

/**
 * The illustration at the top of a service page.
 *
 * WHY DRAWN RATHER THAN PHOTOGRAPHED
 * ----------------------------------
 * These are hand-authored SVG, not stock imagery: no licence to honour, a few
 * kilobytes each against the hundreds a photograph costs, and the colours come
 * from the design tokens so a theme change moves the artwork with it.
 *
 * WHY PRODUCT MOCK-UPS RATHER THAN ABSTRACT SHAPES
 * ------------------------------------------------
 * The first set were abstract diagrams — circles joined by lines. They were
 * tidy but said little: eleven pages, eleven variations on "nodes". Each
 * illustration is now a small piece of the thing the service produces — a chat
 * with an agent calling tools, a pipeline feeding a dashboard, a storefront
 * with a payment split, a phone beside a store badge — built from the same
 * small kit (window, panel, placeholder lines, chips) so they read as one
 * family. Placeholder bars stand in for text on purpose: real words would need
 * translating, would date, and would be read aloud by assistive tech.
 *
 * Decorative: `aria-hidden`, no title, no role. The heading and intro beside
 * it already say what the page is.
 *
 * Gradient and filter ids are namespaced per service, because two of these on
 * one page would otherwise share an id and the second would silently take the
 * first's paint.
 */

/* Shared frame. 400x300 keeps every composition on one coordinate system. */
const VIEWBOX = '0 0 400 300';

/* ---- Palette: tokens only, so the artwork follows the theme ------------- */
const GREEN = 'var(--gt-green-500)';
const GREEN_SOFT = 'var(--gt-green-300)';
const GREEN_DEEP = 'var(--gt-green-700)';
const TEAL = 'var(--gt-teal-500)';
const CYAN = 'var(--gt-cyan-500)';
const AMBER = 'var(--gt-amber-500)';
const RED = 'var(--gt-red-500)';
const BLUE = 'var(--gt-blue-500)';
const SURFACE = 'var(--surface-raised)';
const SUNK = 'var(--surface-sunken)';
const EDGE = 'var(--border-default)';
const EDGE_STRONG = 'var(--border-strong)';
const INK = 'var(--text-primary)';
const BAR = 'var(--gt-neutral-200)';
const BAR_DARK = 'var(--gt-neutral-300)';

/* ---- Shared defs: a wash behind the scene and one soft panel shadow ------ */
const Defs = ({ id }) => (
  <defs>
    <linearGradient
      id={`${id}-wash`}
      gradientUnits="userSpaceOnUse"
      x1="10"
      y1="20"
      x2="390"
      y2="280"
    >
      <stop offset="0%" stopColor="var(--gt-green-400)" stopOpacity="0.3" />
      <stop offset="100%" stopColor="var(--gt-green-500)" stopOpacity="0.04" />
    </linearGradient>
    <linearGradient
      id={`${id}-line`}
      gradientUnits="userSpaceOnUse"
      x1="20"
      y1="0"
      x2="380"
      y2="0"
    >
      <stop offset="0%" stopColor="var(--gt-green-500)" />
      <stop offset="100%" stopColor="var(--gt-green-300)" />
    </linearGradient>
    <linearGradient
      id={`${id}-area`}
      gradientUnits="userSpaceOnUse"
      x1="0"
      y1="0"
      x2="0"
      y2="300"
    >
      <stop offset="0%" stopColor="var(--gt-green-500)" stopOpacity="0.32" />
      <stop offset="100%" stopColor="var(--gt-green-500)" stopOpacity="0" />
    </linearGradient>
    <filter id={`${id}-shadow`} x="-20%" y="-20%" width="140%" height="150%">
      <feDropShadow
        dx="0"
        dy="6"
        stdDeviation="7"
        floodColor="#04120e"
        floodOpacity="0.13"
      />
    </filter>
  </defs>
);

Defs.propTypes = { id: string.isRequired };

/* ---- The kit -------------------------------------------------------------- */

/** The soft blob every scene sits on. */
const Blob = ({ id, cx = 200, cy = 150, rx = 186, ry = 128 }) => (
  <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill={`url(#${id}-wash)`} />
);

Blob.propTypes = {
  id: string.isRequired,
  cx: num,
  cy: num,
  rx: num,
  ry: num,
};

/** A raised card. */
const Panel = ({
  id,
  x,
  y,
  w,
  h,
  r = 12,
  fill = SURFACE,
  shadow = true,
  stroke = EDGE,
}) => (
  <rect
    x={x}
    y={y}
    width={w}
    height={h}
    rx={r}
    fill={fill}
    stroke={stroke}
    strokeWidth="1"
    filter={shadow ? `url(#${id}-shadow)` : undefined}
  />
);

Panel.propTypes = {
  id: string.isRequired,
  x: num.isRequired,
  y: num.isRequired,
  w: num.isRequired,
  h: num.isRequired,
  r: num,
  fill: string,
  shadow: bool,
  stroke: string,
};

/** A placeholder line of text. */
const Bar = ({ x, y, w, h = 6, tone = BAR, o = 1 }) => (
  <rect x={x} y={y} width={w} height={h} rx={h / 2} fill={tone} opacity={o} />
);

Bar.propTypes = {
  x: num.isRequired,
  y: num.isRequired,
  w: num.isRequired,
  h: num,
  tone: string,
  o: num,
};

/** Window chrome: three dots and a divider along the top of a panel. */
const WindowBar = ({ x, y, w }) => (
  <g>
    <circle cx={x + 13} cy={y + 12} r="3" fill={RED} opacity="0.55" />
    <circle cx={x + 24} cy={y + 12} r="3" fill={AMBER} opacity="0.65" />
    <circle cx={x + 35} cy={y + 12} r="3" fill={GREEN} opacity="0.8" />
    <line
      x1={x}
      y1={y + 24}
      x2={x + w}
      y2={y + 24}
      stroke={EDGE}
      strokeWidth="1"
    />
  </g>
);

WindowBar.propTypes = {
  x: num.isRequired,
  y: num.isRequired,
  w: num.isRequired,
};

/** A tick, drawn from its top-left. */
const Tick = ({ x, y, color = GREEN, s = 1 }) => (
  <path
    d={`M${x} ${y + 4 * s}l${3.5 * s} ${3.5 * s} ${7 * s}-${7.5 * s}`}
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    fill="none"
  />
);

Tick.propTypes = {
  x: num.isRequired,
  y: num.isRequired,
  color: string,
  s: num,
};

/** A round badge with something drawn inside it. */
const Badge = ({ cx, cy, r = 11, tone = GREEN, children = null }) => (
  <g>
    <circle cx={cx} cy={cy} r={r} fill={tone} opacity="0.16" />
    <circle cx={cx} cy={cy} r={r} fill="none" stroke={tone} strokeWidth="1.4" />
    {children}
  </g>
);

Badge.propTypes = {
  cx: num.isRequired,
  cy: num.isRequired,
  r: num,
  tone: string,
  children: node,
};

/** A pill-shaped chip. */
const Chip = ({ x, y, w, h = 20, tone = GREEN, solid = false }) => (
  <g>
    <rect
      x={x}
      y={y}
      width={w}
      height={h}
      rx={h / 2}
      fill={tone}
      opacity={solid ? 1 : 0.14}
    />
    {!solid && (
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx={h / 2}
        fill="none"
        stroke={tone}
        strokeWidth="1.2"
      />
    )}
  </g>
);

Chip.propTypes = {
  x: num.isRequired,
  y: num.isRequired,
  w: num.isRequired,
  h: num,
  tone: string,
  solid: bool,
};

/** A four-point spark: the mark for anything the model does. */
const Spark = ({ cx, cy, s = 1, fill = GREEN }) => (
  <path
    d={`M${cx} ${cy - 8 * s}C${cx + 1 * s} ${cy - 2 * s} ${cx + 2 * s} ${cy -
      1 * s} ${cx + 8 * s} ${cy}C${cx + 2 * s} ${cy + 1 * s} ${cx +
      1 * s} ${cy + 2 * s} ${cx} ${cy + 8 * s}C${cx - 1 * s} ${cy +
      2 * s} ${cx - 2 * s} ${cy + 1 * s} ${cx - 8 * s} ${cy}C${cx -
      2 * s} ${cy - 1 * s} ${cx - 1 * s} ${cy - 2 * s} ${cx} ${cy - 8 * s}Z`}
    fill={fill}
  />
);

Spark.propTypes = {
  cx: num.isRequired,
  cy: num.isRequired,
  s: num,
  fill: string,
};

/** A dashed connector. */
const Link = ({ d, tone = GREEN, o = 0.7 }) => (
  <path
    d={d}
    stroke={tone}
    strokeWidth="1.5"
    strokeDasharray="4 5"
    fill="none"
    opacity={o}
    strokeLinecap="round"
  />
);

Link.propTypes = { d: string.isRequired, tone: string, o: num };

/** A database cylinder. */
const Cylinder = ({ x, y, w = 40, h = 38, tone = GREEN }) => (
  <g>
    <path
      d={`M${x} ${y + 8}v${h - 16}a${w / 2} 7 0 0 0 ${w} 0v-${h - 16}`}
      fill={SURFACE}
      stroke={EDGE_STRONG}
      strokeWidth="1.3"
    />
    <ellipse
      cx={x + w / 2}
      cy={y + 8}
      rx={w / 2}
      ry="7"
      fill={SURFACE}
      stroke={EDGE_STRONG}
      strokeWidth="1.3"
    />
    <path
      d={`M${x} ${y + h / 2 + 1}a${w / 2} 7 0 0 0 ${w} 0`}
      fill="none"
      stroke={tone}
      strokeWidth="1.3"
      opacity="0.8"
    />
  </g>
);

Cylinder.propTypes = {
  x: num.isRequired,
  y: num.isRequired,
  w: num,
  h: num,
  tone: string,
};

/* ---- The scenes ----------------------------------------------------------- */

const GRAPHICS = {
  /*
   * The hub: the practice at the centre and the disciplines as tiles, each
   * carrying the cue that identifies it on its own page.
   */
  servicesIndex: id => (
    <g>
      <Blob id={id} />
      {[
        [38, 40],
        [150, 40],
        [262, 40],
        [38, 112],
        [262, 112],
        [38, 184],
        [150, 184],
        [262, 184],
      ].map(([x, y], i) => (
        <g key={`${x}-${y}`}>
          <Panel id={id} x={x} y={y} w={100} h={64} r={14} />
          <Badge
            cx={x + 24}
            cy={y + 24}
            r={11}
            tone={[GREEN, TEAL, CYAN, GREEN, BLUE, TEAL, GREEN, CYAN][i]}
          >
            {i % 4 === 0 && (
              <Spark cx={x + 24} cy={y + 24} s={0.8} fill={GREEN_DEEP} />
            )}
            {i % 4 === 1 && (
              <rect
                x={x + 19}
                y={y + 19}
                width="10"
                height="10"
                rx="2.5"
                fill="none"
                stroke={INK}
                strokeWidth="1.3"
              />
            )}
            {i % 4 === 2 && (
              <path
                d={`M${x + 18} ${y + 27}l5 -6 4 4 5 -7`}
                stroke={INK}
                strokeWidth="1.4"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}
            {i % 4 === 3 && (
              <circle
                cx={x + 24}
                cy={y + 24}
                r="4"
                fill="none"
                stroke={INK}
                strokeWidth="1.3"
              />
            )}
          </Badge>
          <Bar x={x + 44} y={y + 18} w={42} tone={BAR_DARK} />
          <Bar x={x + 44} y={y + 30} w={28} />
          <Bar x={x + 14} y={y + 48} w={70} o={0.7} />
        </g>
      ))}
      <rect x="150" y="112" width="100" height="64" rx="14" fill={GREEN} />
      <circle
        cx="200"
        cy="144"
        r="17"
        fill="none"
        stroke="#fff"
        strokeWidth="2"
        opacity="0.9"
      />
      <path
        d="M192 144h16M200 136v16"
        stroke="#fff"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <Link
        d="M138 144h12M250 144h12M200 104v8M200 176v8"
        tone={GREEN}
        o={0.9}
      />
    </g>
  ),

  /* Agents: a conversation, the tools it calls, and the guardrail around it. */
  aiAgents: id => (
    <g>
      <Blob id={id} />
      <Panel id={id} x="22" y="30" w="226" h="240" r="16" />
      <WindowBar x={22} y={30} w={226} />
      {/* user turn */}
      <rect
        x="118"
        y="72"
        width="112"
        height="28"
        rx="14"
        fill={GREEN}
        opacity="0.16"
      />
      <Bar x={130} y={83} w={72} tone={GREEN_DEEP} o={0.55} />
      {/* assistant turn */}
      <circle cx="42" cy="124" r="10" fill={GREEN} />
      <Spark cx={42} cy={124} s={0.7} fill="#fff" />
      <rect x="58" y="108" width="176" height="62" rx="12" fill={SUNK} />
      <Bar x={70} y={120} w={140} tone={BAR_DARK} />
      <Bar x={70} y={133} w={116} />
      <Bar x={70} y={146} w={84} />
      {/* tool call in flight */}
      <Chip x={58} y={180} w={132} h={24} />
      <circle cx="72" cy="192" r="4" fill={GREEN} />
      <Bar x={82} y={189} w={64} tone={GREEN_DEEP} o={0.6} />
      <path
        d="M170 192l4 0"
        stroke={GREEN}
        strokeWidth="2"
        strokeLinecap="round"
      />
      {/* result */}
      <rect x="58" y="212" width="150" height="26" rx="13" fill={SUNK} />
      <Tick x={70} y={219} />
      <Bar x={88} y={222} w={96} />
      {/* composer */}
      <rect
        x="36"
        y="244"
        width="198"
        height="20"
        rx="10"
        fill={SUNK}
        stroke={EDGE}
      />
      <Bar x={50} y={251} w={74} o={0.8} />
      <circle cx="220" cy="254" r="7" fill={GREEN} />
      <path
        d="M217 254h6M221 251l3 3-3 3"
        stroke="#fff"
        strokeWidth="1.6"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* tools */}
      {[
        [GREEN, 'search'],
        [CYAN, 'db'],
        [AMBER, 'api'],
      ].map(([tone], i) => {
        const y = 40 + i * 62;
        return (
          <g key={tone}>
            <Link
              d={`M248 ${150}C262 ${150} 262 ${y + 24} 276 ${y + 24}`}
              tone={tone}
              o={0.75}
            />
            <Panel id={id} x={276} y={y} w={102} h={50} r={13} />
            <Badge cx={298} cy={y + 25} r={11} tone={tone}>
              <circle cx="298" cy={y + 25} r="3.4" fill={tone} />
            </Badge>
            <Bar x={316} y={y + 17} w={48} tone={BAR_DARK} />
            <Bar x={316} y={y + 29} w={32} />
          </g>
        );
      })}
      {/* guardrail + eval score */}
      <Chip x={276} y={226} w={102} h={30} />
      <path
        d="M294 233l8 3v6c0 4-3 7-8 9-5-2-8-5-8-9v-6z"
        fill="none"
        stroke={GREEN_DEEP}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <Tick x={290} y={238} s={0.6} color={GREEN_DEEP} />
      <rect x="312" y="238" width="54" height="6" rx="3" fill={BAR} />
      <rect x="312" y="238" width="44" height="6" rx="3" fill={GREEN} />
    </g>
  ),

  /* Data and ML: sources into a pipeline, out to a dashboard and a model. */
  dataMl: id => (
    <g>
      <Blob id={id} />
      {/* sources */}
      <Cylinder x={20} y={46} tone={GREEN} />
      <Cylinder x={20} y={112} tone={CYAN} />
      <Cylinder x={20} y={178} tone={AMBER} />
      <Link d="M64 66C84 66 84 142 104 142M64 132C84 132 84 142 104 142M64 198C84 198 84 142 104 142" />
      {/* transform stage */}
      <Panel id={id} x="104" y="104" w="62" h="76" r="14" />
      <path
        d="M122 124h26M122 142h26M122 160h26"
        stroke={BAR_DARK}
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle cx="122" cy="124" r="3" fill={GREEN} />
      <circle cx="148" cy="142" r="3" fill={GREEN} />
      <circle cx="122" cy="160" r="3" fill={GREEN} />
      <Link d="M166 142h30" />
      {/* dashboard */}
      <Panel id={id} x="196" y="36" w="184" h="146" r="16" />
      <Bar x={210} y={50} w={60} tone={BAR_DARK} />
      <Chip x={332} y={46} w={36} h={16} />
      <path
        d="M210 150l22 -22 20 10 24 -34 22 18 24 -40 26 14"
        stroke={GREEN}
        strokeWidth="2.4"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M210 150l22 -22 20 10 24 -34 22 18 24 -40 26 14v38h-138z"
        fill={`url(#${id}-area)`}
      />
      <line x1="210" y1="164" x2="368" y2="164" stroke={EDGE} />
      {[214, 234, 254, 274, 294, 314, 334, 354].map((x, i) => (
        <rect
          key={x}
          x={x}
          y={164 - (6 + ((i * 7) % 15))}
          width="6"
          height={6 + ((i * 7) % 15)}
          rx="2"
          fill={BAR_DARK}
          opacity="0.5"
        />
      ))}
      {/* model + lineage */}
      <Panel id={id} x="196" y="198" w="86" h="70" r="14" />
      <Badge cx={218} cy={222} r={11}>
        <Spark cx={218} cy={222} s={0.7} fill={GREEN_DEEP} />
      </Badge>
      <Bar x={236} y={216} w={34} tone={BAR_DARK} />
      <Bar x={236} y={228} w={24} />
      <rect x="208" y="246" width="62" height="6" rx="3" fill={BAR} />
      <rect x="208" y="246" width="50" height="6" rx="3" fill={GREEN} />
      <Panel id={id} x="294" y="198" w="86" h="70" r="14" />
      <path
        d="M308 224h14M322 224v18h14M322 224v-8h14"
        stroke={BAR_DARK}
        strokeWidth="1.6"
        fill="none"
        strokeLinecap="round"
      />
      <rect
        x="336"
        y="211"
        width="30"
        height="10"
        rx="5"
        fill={GREEN}
        opacity="0.25"
      />
      <rect
        x="336"
        y="237"
        width="30"
        height="10"
        rx="5"
        fill={CYAN}
        opacity="0.25"
      />
      <Bar x={308} y={254} w={56} o={0.7} />
    </g>
  ),

  /* Custom software: the code, the architecture it describes, a green build. */
  customSoftwareDevelopment: id => (
    <g>
      <Blob id={id} />
      <Panel
        id={id}
        x="20"
        y="34"
        w="214"
        h="170"
        r="16"
        fill="var(--surface-inverse)"
        stroke="var(--border-inverse-strong)"
      />
      <circle cx="35" cy="49" r="3" fill={RED} opacity="0.7" />
      <circle cx="46" cy="49" r="3" fill={AMBER} opacity="0.8" />
      <circle cx="57" cy="49" r="3" fill={GREEN} />
      <line x1="20" y1="61" x2="234" y2="61" stroke="var(--border-inverse)" />
      {[
        [34, 74, 54, GREEN_SOFT],
        [48, 88, 96, CYAN],
        [48, 102, 70, AMBER],
        [62, 116, 120, 'var(--gt-neutral-300)'],
        [62, 130, 84, GREEN_SOFT],
        [48, 144, 44, 'var(--gt-neutral-300)'],
        [34, 158, 38, GREEN_SOFT],
        [34, 172, 86, CYAN],
        [48, 186, 60, AMBER],
      ].map(([x, y, w, tone]) => (
        <rect
          key={`${x}-${y}`}
          x={x}
          y={y}
          width={w}
          height="5"
          rx="2.5"
          fill={tone}
          opacity="0.85"
        />
      ))}
      <rect x="116" y="170" width="2" height="10" fill={GREEN} />
      {/* architecture */}
      <Panel id={id} x="214" y="86" w="170" h="132" r="16" />
      <rect
        x="236"
        y="104"
        width="44"
        height="26"
        rx="8"
        fill={GREEN}
        opacity="0.18"
        stroke={GREEN}
      />
      <rect
        x="320"
        y="104"
        width="44"
        height="26"
        rx="8"
        fill={SUNK}
        stroke={EDGE_STRONG}
      />
      <rect
        x="236"
        y="170"
        width="44"
        height="26"
        rx="8"
        fill={SUNK}
        stroke={EDGE_STRONG}
      />
      <rect
        x="320"
        y="170"
        width="44"
        height="26"
        rx="8"
        fill={CYAN}
        opacity="0.16"
        stroke={CYAN}
      />
      <path
        d="M280 117h40M258 130v40M342 130v40M280 183h40"
        stroke={BAR_DARK}
        strokeWidth="1.6"
        fill="none"
        strokeLinecap="round"
      />
      <circle
        cx="300"
        cy="150"
        r="9"
        fill={SURFACE}
        stroke={GREEN}
        strokeWidth="1.6"
      />
      <path
        d="M296 150h8M300 146v8"
        stroke={GREEN_DEEP}
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <Link d="M258 130L292 144M342 130L308 144M258 170L292 156M342 170L308 156" />
      {/* build status */}
      <Chip x={48} y={224} w={134} h={30} />
      <circle cx="66" cy="239" r="8" fill={GREEN} />
      <Tick x={62} y={235} s={0.6} color="#fff" />
      <Bar x={82} y={232} w={60} tone={GREEN_DEEP} o={0.6} />
      <Bar x={82} y={244} w={36} tone={GREEN_DEEP} o={0.35} />
      <Chip x={192} y={232} w={64} h={22} tone={CYAN} />
      <Bar x={204} y={240} w={40} tone={CYAN} o={0.6} />
    </g>
  ),

  /* Marketplace: two sides, one listing, and the money splitting between them. */
  marketplaces: id => (
    <g>
      <Blob id={id} />
      {/* supply */}
      <Panel id={id} x="16" y="40" w="110" h="108" r="16" />
      <circle
        cx="46"
        cy="72"
        r="15"
        fill={GREEN}
        opacity="0.2"
        stroke={GREEN}
      />
      <circle cx="46" cy="68" r="5" fill={GREEN_DEEP} opacity="0.8" />
      <path
        d="M36 82c2-6 18-6 20 0"
        stroke={GREEN_DEEP}
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
      />
      <Bar x={68} y={62} w={42} tone={BAR_DARK} />
      <Bar x={68} y={74} w={28} />
      <Chip x={28} y={98} w={86} h={18} />
      <Bar x={38} y={104} w={52} tone={GREEN_DEEP} o={0.55} />
      <Bar x={28} y={128} w={72} o={0.7} />
      {/* demand */}
      <Panel id={id} x="274" y="40" w="110" h="108" r="16" />
      <circle cx="304" cy="72" r="15" fill={CYAN} opacity="0.2" stroke={CYAN} />
      <circle cx="304" cy="68" r="5" fill={CYAN} opacity="0.9" />
      <path
        d="M294 82c2-6 18-6 20 0"
        stroke={CYAN}
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
      />
      <Bar x={326} y={62} w={42} tone={BAR_DARK} />
      <Bar x={326} y={74} w={28} />
      {[0, 1, 2, 3, 4].map(i => (
        <circle
          key={i}
          cx={294 + i * 14}
          cy={106}
          r="4.5"
          fill={AMBER}
          opacity={i < 4 ? 0.95 : 0.3}
        />
      ))}
      <Bar x={286} y={128} w={72} o={0.7} />
      {/* the listing between them */}
      <Panel id={id} x="118" y="22" w="164" h="128" r="16" />
      <rect x="130" y="34" width="140" height="54" rx="10" fill={SUNK} />
      <path
        d="M130 78l34 -24 26 18 22 -14 58 20v10a10 10 0 0 1-10 10h-120a10 10 0 0 1-10-10z"
        fill={GREEN}
        opacity="0.2"
      />
      <circle cx="248" cy="52" r="7" fill={AMBER} opacity="0.8" />
      <Bar x={130} y={98} w={86} tone={BAR_DARK} />
      <Bar x={130} y={110} w={60} />
      <Chip x={226} y={100} w={44} h={22} tone={GREEN} solid />
      <Bar x={236} y={108} w={24} tone="#fff" o={0.9} />
      <rect
        x="130"
        y="126"
        width="140"
        height="14"
        rx="7"
        fill={GREEN}
        opacity="0.18"
      />
      <Bar x={168} y={130} w={64} tone={GREEN_DEEP} o={0.5} />
      {/* payment flow */}
      <Link d="M72 148v22c0 14 40 14 60 14" />
      <Link d="M329 148v22c0 14 -40 14 -60 14" tone={CYAN} />
      <Panel id={id} x="40" y="176" w="320" h="104" r="18" />
      <Badge cx={72} cy={206} r={13} tone={CYAN}>
        <rect
          x="65"
          y="201"
          width="14"
          height="10"
          rx="2.5"
          fill="none"
          stroke={INK}
          strokeWidth="1.3"
        />
        <line
          x1="65"
          y1="205"
          x2="79"
          y2="205"
          stroke={INK}
          strokeWidth="1.3"
        />
      </Badge>
      <Bar x={94} y={198} w={58} tone={BAR_DARK} />
      <Bar x={94} y={210} w={36} />
      <path
        d="M170 206h28"
        stroke={GREEN}
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M194 201l6 5-6 5"
        stroke={GREEN}
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect x="210" y="190" width="68" height="32" rx="10" fill={GREEN} />
      <Spark cx={226} cy={206} s={0.6} fill="#fff" />
      <Bar x={238} y={202} w={30} tone="#fff" o={0.9} />
      <Bar x={238} y={212} w={20} tone="#fff" o={0.55} />
      <path
        d="M278 206h16"
        stroke={GREEN}
        strokeWidth="2"
        strokeLinecap="round"
      />
      <Badge cx={316} cy={206} r={13} tone={GREEN}>
        <Tick x={311} y={202} s={0.7} color={GREEN_DEEP} />
      </Badge>
      {/* the split */}
      <rect x="60" y="244" width="280" height="12" rx="6" fill={BAR} />
      <rect x="60" y="244" width="232" height="12" rx="6" fill={GREEN} />
      <rect x="292" y="244" width="48" height="12" rx="6" fill={CYAN} />
      <Bar x={60} y={264} w={50} o={0.7} />
      <Bar x={290} y={264} w={50} o={0.7} />
    </g>
  ),

  /* Sharetribe: a storefront, the template beside it, and the flow beneath. */
  sharetribe: id => (
    <g>
      <Blob id={id} />
      <Panel id={id} x="16" y="28" w="252" h="190" r="16" />
      <WindowBar x={16} y={28} w={252} />
      <rect x="86" y="42" width="120" height="14" rx="7" fill={SUNK} />
      <Bar x={98} y={46} w={60} o={0.8} />
      {[
        [28, 70, GREEN],
        [92, 70, CYAN],
        [156, 70, AMBER],
        [28, 140, TEAL],
        [92, 140, GREEN],
        [156, 140, BLUE],
      ].map(([x, y, tone]) => (
        <g key={`${x}-${y}`}>
          <rect
            x={x}
            y={y}
            width="58"
            height="40"
            rx="8"
            fill={tone}
            opacity="0.18"
          />
          <path
            d={`M${x} ${y +
              34}l16 -14 12 8 12 -10 22 14v2a8 8 0 0 1-8 8h-46a8 8 0 0 1-8-8z`}
            fill={tone}
            opacity="0.35"
          />
          <Bar x={x} y={y + 47} w={46} tone={BAR_DARK} />
          <Bar x={x} y={y + 58} w={30} />
        </g>
      ))}
      {/* customisation panel */}
      <Panel id={id} x="226" y="64" w="158" h="154" r="16" />
      <Bar x={240} y={78} w={56} tone={BAR_DARK} />
      {[GREEN, CYAN, AMBER, BLUE].map((tone, i) => (
        <circle
          key={tone}
          cx={248 + i * 24}
          cy={104}
          r="9"
          fill={tone}
          stroke={i === 0 ? INK : 'none'}
          strokeWidth="1.5"
        />
      ))}
      <Bar x={240} y={128} w={44} />
      <rect x="240" y="142" width="130" height="6" rx="3" fill={BAR} />
      <rect x="240" y="142" width="84" height="6" rx="3" fill={GREEN} />
      <circle
        cx="324"
        cy="145"
        r="6"
        fill={SURFACE}
        stroke={GREEN}
        strokeWidth="2"
      />
      <Bar x={240} y={164} w={52} />
      <rect x="240" y="178" width="34" height="16" rx="8" fill={GREEN} />
      <circle cx="266" cy="186" r="6" fill="#fff" />
      <rect x="282" y="178" width="34" height="16" rx="8" fill={BAR} />
      <circle cx="290" cy="186" r="6" fill="#fff" />
      <Bar x={240} y={204} w={90} o={0.6} />
      {/* transaction flow */}
      <Panel id={id} x="16" y="234" w="368" h="52" r="16" />
      {[
        [32, 'Enquiry', 66],
        [118, 'Accepted', 74],
        [214, 'Paid', 56],
        [290, 'Delivered', 80],
      ].map(([x, , w], i) => (
        <g key={x}>
          <Chip
            x={x}
            y={248}
            w={w}
            h={24}
            tone={i === 2 ? GREEN : i === 3 ? CYAN : EDGE_STRONG}
            solid={i === 2}
          />
          <Bar
            x={x + 12}
            y={257}
            w={w - 24}
            tone={i === 2 ? '#fff' : BAR_DARK}
            o={i === 2 ? 0.9 : 1}
          />
          {i < 3 && (
            <path
              d={`M${x + w + 4} 260h${i === 0 ? 14 : i === 1 ? 20 : 14}`}
              stroke={GREEN}
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          )}
        </g>
      ))}
    </g>
  ),

  /* Web: a page in a browser, and the scores that say it is any good. */
  webDevelopment: id => (
    <g>
      <Blob id={id} />
      <Panel id={id} x="16" y="30" w="280" h="214" r="16" />
      <WindowBar x={16} y={30} w={280} />
      <rect x="64" y="42" width="180" height="14" rx="7" fill={SUNK} />
      <circle cx="74" cy="49" r="2.6" fill={GREEN} />
      <Bar x={84} y={46} w={90} o={0.8} />
      {/* page */}
      <Bar x={30} y={76} w={38} tone={BAR_DARK} />
      <Bar x={210} y={76} w={20} />
      <Bar x={238} y={76} w={20} />
      <Bar x={266} y={76} w={20} />
      <rect
        x="30"
        y="96"
        width="156"
        height="14"
        rx="7"
        fill={INK}
        opacity="0.85"
      />
      <rect
        x="30"
        y="116"
        width="112"
        height="14"
        rx="7"
        fill={INK}
        opacity="0.85"
      />
      <Bar x={30} y={142} w={150} />
      <Bar x={30} y={154} w={128} />
      <rect x="30" y="172" width="74" height="22" rx="11" fill={GREEN} />
      <Bar x={44} y={180} w={46} tone="#fff" o={0.9} />
      <rect
        x="204"
        y="94"
        width="82"
        height="100"
        rx="12"
        fill={GREEN}
        opacity="0.16"
      />
      <path
        d="M204 170l24 -24 18 14 20 -22 20 18v18a12 12 0 0 1-12 12h-58a12 12 0 0 1-12-12z"
        fill={GREEN}
        opacity="0.35"
      />
      <circle cx="262" cy="116" r="8" fill={AMBER} opacity="0.85" />
      {[28, 112, 196].map(x => (
        <g key={x}>
          <rect x={x + 2} y="208" width="80" height="26" rx="8" fill={SUNK} />
          <Bar x={x + 12} y={215} w={44} tone={BAR_DARK} />
          <Bar x={x + 12} y={225} w={28} />
        </g>
      ))}
      {/* scores */}
      <Panel id={id} x="288" y="64" w="96" h="196" r="16" />
      {[
        [336, 104, 0.98, GREEN],
        [336, 164, 0.94, TEAL],
        [336, 224, 0.96, CYAN],
      ].map(([cx, cy, p, tone]) => {
        const r = 19;
        const c = 2 * Math.PI * r;
        return (
          <g key={cy}>
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={BAR}
              strokeWidth="5"
            />
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={tone}
              strokeWidth="5"
              strokeLinecap="round"
              strokeDasharray={`${c * p} ${c}`}
              transform={`rotate(-90 ${cx} ${cy})`}
            />
            <Tick x={cx - 5} y={cy - 5} s={0.7} color={tone} />
          </g>
        );
      })}
      <Bar x={304} y={134} w={64} o={0.7} />
      <Bar x={304} y={194} w={64} o={0.7} />
      <Chip x={16} y={256} w={92} h={24} tone={CYAN} />
      <path
        d="M33 264l-6 4 6 4M44 264l6 4-6 4M41 263l-5 10"
        stroke={CYAN}
        strokeWidth="1.6"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Bar x={56} y={265} w={40} tone={CYAN} o={0.55} />
    </g>
  ),

  /* Mobile: two screens, a notification, and the store the app ships to. */
  mobileAppDevelopment: id => (
    <g>
      <Blob id={id} />
      {/* back phone */}
      <rect
        x="170"
        y="22"
        width="132"
        height="256"
        rx="24"
        fill={SURFACE}
        stroke={EDGE_STRONG}
        strokeWidth="1.5"
        filter={`url(#${id}-shadow)`}
      />
      <rect x="180" y="40" width="112" height="220" rx="16" fill={SUNK} />
      <Bar x={192} y={54} w={50} tone={BAR_DARK} />
      <path
        d="M192 140l20 -22 18 12 22 -34 20 16v52h-80z"
        fill={`url(#${id}-area)`}
      />
      <path
        d="M192 140l20 -22 18 12 22 -34 20 16"
        stroke={GREEN}
        strokeWidth="2.4"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {[0, 1, 2].map(i => (
        <g key={i}>
          <rect
            x="192"
            y={162 + i * 30}
            width="88"
            height="24"
            rx="8"
            fill={SURFACE}
          />
          <circle
            cx="206"
            cy={174 + i * 30}
            r="6"
            fill={[GREEN, CYAN, AMBER][i]}
            opacity="0.5"
          />
          <Bar x={218} y={170 + i * 30} w={44} tone={BAR_DARK} />
        </g>
      ))}
      {/* front phone */}
      <rect
        x="60"
        y="48"
        width="132"
        height="244"
        rx="24"
        fill={SURFACE}
        stroke={INK}
        strokeWidth="1.8"
        filter={`url(#${id}-shadow)`}
      />
      <rect x="70" y="66" width="112" height="208" rx="16" fill={SUNK} />
      <rect
        x="104"
        y="56"
        width="44"
        height="8"
        rx="4"
        fill={INK}
        opacity="0.85"
      />
      <rect x="80" y="82" width="92" height="64" rx="12" fill={GREEN} />
      <Spark cx={100} cy={102} s={0.8} fill="#fff" />
      <Bar x={116} y={98} w={40} tone="#fff" o={0.9} />
      <Bar x={92} y={122} w={60} tone="#fff" o={0.6} />
      <Bar x={92} y={132} w={38} tone="#fff" o={0.4} />
      {[0, 1, 2].map(i => (
        <g key={i}>
          <rect
            x="80"
            y={156 + i * 32}
            width="92"
            height="26"
            rx="9"
            fill={SURFACE}
          />
          <circle
            cx="95"
            cy={169 + i * 32}
            r="7"
            fill={[CYAN, GREEN, AMBER][i]}
            opacity="0.35"
          />
          <Bar x={108} y={164 + i * 32} w={46} tone={BAR_DARK} />
          <Bar x={108} y={174 + i * 32} w={28} />
        </g>
      ))}
      <rect x="80" y="254" width="92" height="12" rx="6" fill={SURFACE} />
      {[0, 1, 2, 3].map(i => (
        <circle
          key={i}
          cx={96 + i * 20}
          cy="260"
          r="3"
          fill={i === 0 ? GREEN : BAR_DARK}
        />
      ))}
      {/* notification */}
      <Panel id={id} x="198" y="62" w="186" h="42" r="14" />
      <Badge cx={220} cy={83} r={11}>
        <path
          d="M215 86h10l-1.6-2v-3a3.4 3.4 0 0 0-6.8 0v3z"
          fill={GREEN_DEEP}
        />
      </Badge>
      <Bar x={238} y={76} w={70} tone={BAR_DARK} />
      <Bar x={238} y={88} w={102} />
      {/* stores */}
      <Chip x={252} y={232} w={128} h={26} tone={INK} solid />
      <path d="M270 238l8 7-8 7z" fill="#fff" opacity="0.9" />
      <Bar x={288} y={242} w={72} tone="#fff" o={0.75} />
      <Chip x={252} y={264} w={128} h={22} tone={GREEN} />
      <circle cx="268" cy="275" r="5" fill={GREEN} opacity="0.7" />
      <Bar x={280} y={272} w={72} tone={GREEN_DEEP} o={0.5} />
    </g>
  ),

  /* Cloud and DevOps: a pipeline to production, and the dashboard watching it. */
  cloudSolutions: id => (
    <g>
      <Blob id={id} />
      {/* cloud with racks */}
      <path
        d="M96 108a30 30 0 0 1 8-58 38 38 0 0 1 72-8 32 32 0 0 1 24 66z"
        fill={SURFACE}
        stroke={EDGE_STRONG}
        strokeWidth="1.5"
        filter={`url(#${id}-shadow)`}
      />
      {[0, 1, 2].map(i => (
        <g key={i}>
          <rect
            x={120 + i * 28}
            y="58"
            width="20"
            height="38"
            rx="5"
            fill={SUNK}
            stroke={EDGE_STRONG}
          />
          <circle cx={130 + i * 28} cy="68" r="2.4" fill={GREEN} />
          <line
            x1={124 + i * 28}
            y1="78"
            x2={136 + i * 28}
            y2="78"
            stroke={BAR_DARK}
            strokeWidth="1.6"
            strokeLinecap="round"
          />
          <line
            x1={124 + i * 28}
            y1="86"
            x2={136 + i * 28}
            y2="86"
            stroke={BAR_DARK}
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </g>
      ))}
      <Chip x={216} y={44} w={52} h={22} tone={CYAN} />
      <Bar x={228} y={52} w={28} tone={CYAN} o={0.6} />
      <Chip x={216} y={74} w={52} h={22} />
      <Bar x={228} y={82} w={28} tone={GREEN_DEEP} o={0.55} />
      <Link d="M268 55h22M268 85h22" />
      {/* regions */}
      <Panel id={id} x="290" y="30" w="94" h="86" r="14" />
      {[
        [312, 56],
        [352, 52],
        [330, 90],
      ].map(([x, y]) => (
        <g key={`${x}-${y}`}>
          <circle cx={x} cy={y} r="9" fill={GREEN} opacity="0.22" />
          <circle cx={x} cy={y} r="3.6" fill={GREEN} />
        </g>
      ))}
      <path
        d="M312 56l40 -4M312 56l18 34M352 52l-22 38"
        stroke={BAR_DARK}
        strokeWidth="1.3"
        strokeDasharray="3 4"
      />
      {/* pipeline */}
      <Panel id={id} x="16" y="130" w="368" h="62" r="16" />
      {[
        ['commit', 28],
        ['build', 108],
        ['test', 188],
        ['deploy', 268],
      ].map(([, x], i) => (
        <g key={x}>
          <rect
            x={x}
            y="144"
            width="70"
            height="34"
            rx="11"
            fill={i === 3 ? GREEN : SUNK}
            stroke={i === 3 ? 'none' : EDGE_STRONG}
          />
          <circle
            cx={x + 17}
            cy="161"
            r="7"
            fill={i === 3 ? '#fff' : GREEN}
            opacity={i === 3 ? 0.9 : 0.2}
          />
          <Tick
            x={x + 13}
            y={157}
            s={0.55}
            color={i === 3 ? GREEN_DEEP : GREEN}
          />
          <Bar
            x={x + 30}
            y={158}
            w={30}
            tone={i === 3 ? '#fff' : BAR_DARK}
            o={i === 3 ? 0.9 : 1}
          />
          {i < 3 && (
            <path
              d={`M${x + 72} 161h6`}
              stroke={GREEN}
              strokeWidth="2"
              strokeLinecap="round"
            />
          )}
        </g>
      ))}
      <Bar x={348} y={153} w={24} o={0.5} />
      {/* monitoring */}
      <Panel id={id} x="16" y="206" w="236" h="80" r="16" />
      <path
        d="M30 266l20 -10 18 4 22 -22 20 12 24 -26 22 14 22 -12"
        stroke={GREEN}
        strokeWidth="2.2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M30 266l20 -10 18 4 22 -22 20 12 24 -26 22 14 22 -12v22h-148z"
        fill={`url(#${id}-area)`}
      />
      <Bar x={30} y={216} w={58} tone={BAR_DARK} />
      <Panel id={id} x="264" y="206" w="120" h="80" r="16" />
      <Bar x={278} y={218} w={44} tone={BAR_DARK} />
      {[0, 1, 2, 3, 4].map(i => (
        <rect
          key={i}
          x={278 + i * 19}
          y={274 - (10 + ((i * 11) % 28))}
          width="12"
          height={10 + ((i * 11) % 28)}
          rx="3"
          fill={i === 3 ? AMBER : GREEN}
          opacity={i === 3 ? 0.9 : 0.55}
        />
      ))}
    </g>
  ),

  /* UI/UX: a canvas with frames, a palette, a cursor and a pen path. */
  uiUxDesign: id => (
    <g>
      <Blob id={id} />
      <Panel id={id} x="16" y="24" w="368" h="252" r="18" fill={SUNK} />
      {/* toolbar */}
      <Panel id={id} x="32" y="38" w="60" h="224" r="14" shadow={false} />
      {[0, 1, 2, 3, 4].map(i => (
        <rect
          key={i}
          x="46"
          y={52 + i * 32}
          width="32"
          height="22"
          rx="7"
          fill={i === 1 ? GREEN : SUNK}
          opacity={i === 1 ? 1 : 0.9}
        />
      ))}
      <path
        d="M54 63h16M54 95h16M54 127h16"
        stroke={BAR_DARK}
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle
        cx="62"
        cy="159"
        r="5"
        fill="none"
        stroke={BAR_DARK}
        strokeWidth="1.8"
      />
      <rect
        x="55"
        y="187"
        width="14"
        height="10"
        rx="2.5"
        fill="none"
        stroke={BAR_DARK}
        strokeWidth="1.8"
      />
      {/* frames */}
      <Panel id={id} x="108" y="46" w="104" h="170" r="12" />
      <rect
        x="118"
        y="58"
        width="84"
        height="48"
        rx="8"
        fill={GREEN}
        opacity="0.2"
      />
      <Bar x={118} y={116} w={64} tone={BAR_DARK} />
      <Bar x={118} y={128} w={48} />
      <rect x="118" y="146" width="84" height="22" rx="11" fill={GREEN} />
      <Bar x={140} y={154} w={40} tone="#fff" o={0.9} />
      <rect x="118" y="178" width="84" height="26" rx="8" fill={SUNK} />
      <Panel id={id} x="226" y="64" w="144" h="100" r="12" />
      <rect
        x="238"
        y="76"
        width="56"
        height="40"
        rx="8"
        fill={CYAN}
        opacity="0.2"
      />
      <Bar x={304} y={80} w={52} tone={BAR_DARK} />
      <Bar x={304} y={92} w={40} />
      <Bar x={304} y={104} w={46} />
      <Bar x={238} y={128} w={110} />
      <Bar x={238} y={140} w={80} />
      {/* selection + cursor */}
      <rect
        x="226"
        y="64"
        width="144"
        height="100"
        rx="12"
        fill="none"
        stroke={GREEN}
        strokeWidth="1.6"
        strokeDasharray="5 4"
      />
      {[
        [226, 64],
        [370, 64],
        [226, 164],
        [370, 164],
      ].map(([x, y]) => (
        <rect
          key={`${x}-${y}`}
          x={x - 4}
          y={y - 4}
          width="8"
          height="8"
          rx="2"
          fill={SURFACE}
          stroke={GREEN}
          strokeWidth="1.6"
        />
      ))}
      <path d="M340 176l-4 22 6 -5 5 9 5-3-5-9 8-1z" fill={INK} />
      <Chip x={354} y={196} w={22} h={16} tone={GREEN} solid />
      {/* pen path */}
      <path
        d="M232 244C262 196 286 270 318 232S352 226 366 240"
        stroke={GREEN}
        strokeWidth="2.4"
        fill="none"
        strokeLinecap="round"
      />
      {[
        [232, 244],
        [318, 232],
        [366, 240],
      ].map(([x, y]) => (
        <rect
          key={`${x}-${y}`}
          x={x - 4}
          y={y - 4}
          width="8"
          height="8"
          rx="2"
          fill={SURFACE}
          stroke={GREEN}
          strokeWidth="1.6"
        />
      ))}
      {/* palette + type */}
      {[GREEN, CYAN, AMBER, BLUE, TEAL].map((tone, i) => (
        <circle key={tone} cx={124 + i * 20} cy={238} r="8" fill={tone} />
      ))}
      <text
        x="116"
        y="268"
        fontFamily="var(--font-display)"
        fontWeight="600"
        fontSize="15"
        fill={INK}
        opacity="0.75"
      >
        Aa
      </text>
    </g>
  ),

  /* QA: a test run going green, one failure caught, and the device matrix. */
  qualityAssurance: id => (
    <g>
      <Blob id={id} />
      <Panel id={id} x="16" y="30" w="228" h="240" r="16" />
      <Bar x={30} y={46} w={74} tone={BAR_DARK} />
      <Chip x={186} y={42} w={44} h={18} />
      <Bar x={194} y={48} w={28} tone={GREEN_DEEP} o={0.55} />
      <line x1="16" y1="70" x2="244" y2="70" stroke={EDGE} />
      {[
        [GREEN, 120],
        [GREEN, 96],
        [GREEN, 140],
        [RED, 106],
        [GREEN, 88],
        [GREEN, 130],
        [AMBER, 100],
      ].map(([tone, w], i) => {
        const y = 82 + i * 26;
        return (
          <g key={y}>
            <circle cx="36" cy={y + 8} r="8" fill={tone} opacity="0.18" />
            {tone === GREEN && <Tick x={32} y={y + 4} s={0.55} color={GREEN} />}
            {tone === RED && (
              <path
                d={`M32.5 ${y + 4.5}l7 7M39.5 ${y + 4.5}l-7 7`}
                stroke={RED}
                strokeWidth="2"
                strokeLinecap="round"
              />
            )}
            {tone === AMBER && (
              <path
                d={`M36 ${y + 4}v5M36 ${y + 12}v.5`}
                stroke={AMBER}
                strokeWidth="2"
                strokeLinecap="round"
              />
            )}
            <Bar
              x={54}
              y={y + 5}
              w={w}
              tone={tone === RED ? RED : BAR_DARK}
              o={tone === RED ? 0.5 : 1}
            />
            <Bar x={196} y={y + 5} w={32} o={0.6} />
          </g>
        );
      })}
      {/* coverage ring */}
      <Panel id={id} x="260" y="30" w="124" h="112" r="16" />
      {(() => {
        const r = 30;
        const c = 2 * Math.PI * r;
        return (
          <g>
            <circle
              cx="322"
              cy="84"
              r={r}
              fill="none"
              stroke={BAR}
              strokeWidth="9"
            />
            <circle
              cx="322"
              cy="84"
              r={r}
              fill="none"
              stroke={GREEN}
              strokeWidth="9"
              strokeLinecap="round"
              strokeDasharray={`${c * 0.86} ${c}`}
              transform="rotate(-90 322 84)"
            />
            <Tick x={314} y={77} s={1.1} color={GREEN_DEEP} />
          </g>
        );
      })()}
      <Bar x={288} y={124} w={68} o={0.7} />
      {/* device matrix */}
      <Panel id={id} x="260" y="156" w="124" h="114" r="16" />
      <Bar x={274} y={170} w={52} tone={BAR_DARK} />
      {[0, 1, 2, 3, 4, 5, 6, 7].map(i => {
        const x = 274 + (i % 4) * 26;
        const y = 188 + Math.floor(i / 4) * 36;
        const bad = i === 5;
        return (
          <g key={i}>
            <rect
              x={x}
              y={y}
              width="20"
              height="28"
              rx="5"
              fill={SUNK}
              stroke={bad ? AMBER : EDGE_STRONG}
              strokeWidth="1.3"
            />
            <circle
              cx={x + 10}
              cy={y + 14}
              r="4"
              fill={bad ? AMBER : GREEN}
              opacity="0.8"
            />
          </g>
        );
      })}
      <rect x="274" y="256" width="96" height="5" rx="2.5" fill={BAR} />
      <rect x="274" y="256" width="78" height="5" rx="2.5" fill={GREEN} />
    </g>
  ),

  /* Product management: a roadmap above, a board below, a card in motion. */
  productManagement: id => (
    <g>
      <Blob id={id} />
      <Panel id={id} x="16" y="26" w="368" h="98" r="16" />
      <Bar x={30} y={40} w={58} tone={BAR_DARK} />
      {['Q1', 'Q2', 'Q3', 'Q4'].map((q, i) => (
        <g key={q}>
          <line
            x1={96 + i * 70}
            y1="58"
            x2={96 + i * 70}
            y2="112"
            stroke={EDGE}
            strokeDasharray="3 4"
          />
          <Bar x={102 + i * 70} y={42} w={24} o={0.7} />
        </g>
      ))}
      <rect x="30" y="62" width="130" height="14" rx="7" fill={GREEN} />
      <rect
        x="120"
        y="82"
        width="110"
        height="14"
        rx="7"
        fill={CYAN}
        opacity="0.85"
      />
      <rect
        x="200"
        y="102"
        width="120"
        height="14"
        rx="7"
        fill={AMBER}
        opacity="0.85"
      />
      <path d="M258 58v-4l8 5-8 5v-4" fill="none" />
      <path
        d="M320 112l10 -10 10 10-10 10z"
        fill={GREEN}
        transform="translate(8 -2) scale(0.9)"
      />
      {/* board */}
      {[0, 1, 2].map(col => {
        const x = 16 + col * 126;
        const tones = [BAR_DARK, GREEN, TEAL];
        const counts = [3, 2, 2];
        return (
          <g key={col}>
            <rect
              x={x}
              y="138"
              width="116"
              height="144"
              rx="14"
              fill={SUNK}
              stroke={EDGE}
            />
            <Bar x={x + 12} y={150} w={46} tone={tones[col]} />
            <circle
              cx={x + 100}
              cy="153"
              r="7"
              fill={tones[col]}
              opacity="0.2"
            />
            {Array.from({ length: counts[col] }).map((_, i) => {
              const y = 170 + i * 36;
              const lifted = col === 1 && i === 0;
              return (
                <g
                  key={y}
                  transform={
                    lifted ? 'rotate(-3 ' + (x + 58) + ' ' + y + ')' : undefined
                  }
                >
                  <rect
                    x={x + 10}
                    y={y}
                    width="96"
                    height="30"
                    rx="9"
                    fill={SURFACE}
                    stroke={lifted ? GREEN : EDGE}
                    strokeWidth={lifted ? 1.6 : 1}
                    filter={lifted ? `url(#${id}-shadow)` : undefined}
                  />
                  <Bar
                    x={x + 20}
                    y={y + 8}
                    w={lifted ? 62 : 52}
                    tone={BAR_DARK}
                  />
                  <Bar x={x + 20} y={y + 18} w={34} />
                  <circle
                    cx={x + 92}
                    cy={y + 20}
                    r="5"
                    fill={[GREEN, CYAN, AMBER][(i + col) % 3]}
                    opacity="0.7"
                  />
                </g>
              );
            })}
          </g>
        );
      })}
    </g>
  ),

  /* Maintenance: uptime, a patch going out and the queue being worked down. */
  softwareMaintenance: id => (
    <g>
      <Blob id={id} />
      <Panel id={id} x="16" y="28" w="368" h="84" r="16" />
      <Badge cx={44} cy={56} r={12}>
        <Tick x={39} y={52} s={0.7} color={GREEN_DEEP} />
      </Badge>
      <Bar x={64} y={48} w={70} tone={BAR_DARK} />
      <Bar x={64} y={60} w={44} />
      <Chip x={316} y={46} w={56} h={20} />
      <Bar x={326} y={53} w={36} tone={GREEN_DEEP} o={0.55} />
      {Array.from({ length: 36 }).map((_, i) => (
        <rect
          key={i}
          x={30 + i * 9.7}
          y="84"
          width="6"
          height="18"
          rx="2.5"
          fill={i === 22 ? AMBER : GREEN}
          opacity={i === 22 ? 0.95 : 0.7}
        />
      ))}
      {/* patches */}
      <Panel id={id} x="16" y="128" w="196" h="154" r="16" />
      <Bar x={30} y={142} w={66} tone={BAR_DARK} />
      {[0, 1, 2].map(i => {
        const y = 162 + i * 38;
        return (
          <g key={y}>
            <rect x="28" y={y} width="172" height="30" rx="9" fill={SUNK} />
            <Badge cx={44} cy={y + 15} r={8} tone={[GREEN, CYAN, AMBER][i]}>
              <path
                d={`M41 ${y + 15}l2.4 2.4 4.6-5`}
                stroke={INK}
                strokeWidth="1.3"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </Badge>
            <Bar x={60} y={y + 8} w={60} tone={BAR_DARK} />
            <Bar x={60} y={y + 18} w={38} />
            <Chip
              x={150}
              y={y + 6}
              w={40}
              h={18}
              tone={[GREEN, CYAN, AMBER][i]}
            />
          </g>
        );
      })}
      {/* queue */}
      <Panel id={id} x="224" y="128" w="160" h="154" r="16" />
      <Bar x={238} y={142} w={50} tone={BAR_DARK} />
      {(() => {
        const r = 30;
        const c = 2 * Math.PI * r;
        return (
          <g>
            <circle
              cx="304"
              cy="206"
              r={r}
              fill="none"
              stroke={BAR}
              strokeWidth="9"
            />
            <circle
              cx="304"
              cy="206"
              r={r}
              fill="none"
              stroke={GREEN}
              strokeWidth="9"
              strokeLinecap="round"
              strokeDasharray={`${c * 0.72} ${c}`}
              transform="rotate(-90 304 206)"
            />
            <path
              d="M295 206l7 7 12-14"
              stroke={GREEN_DEEP}
              strokeWidth="3"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        );
      })()}
      <Bar x={262} y={250} w={84} o={0.7} />
      <Bar x={276} y={262} w={56} o={0.5} />
    </g>
  ),
};

/**
 * The illustration for one service, or nothing when the id has none.
 *
 * @param {object} props
 * @param {string} props.serviceId
 * @param {string} [props.className]
 */
const ServiceGraphic = ({ serviceId, className = null }) => {
  const draw = GRAPHICS[serviceId];
  if (!draw) return null;

  return (
    <svg
      className={className}
      viewBox={VIEWBOX}
      fill="none"
      aria-hidden="true"
      focusable="false"
      preserveAspectRatio="xMidYMid meet"
      style={{ overflow: 'visible' }}
    >
      <Defs id={serviceId} />
      {draw(serviceId)}
    </svg>
  );
};

ServiceGraphic.propTypes = {
  serviceId: string.isRequired,
  className: string,
};

export default ServiceGraphic;
